import React, { useState, useEffect } from 'react';
import { RadioGroup, Radio, Stack, Flex, Text, MenuItem } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSort } from '@fortawesome/pro-regular-svg-icons';
import {
  Button,
  ButtonText,
  Menu,
  MenuButton,
  MenuList
} from '@morressier/alembic';

export interface SortField {
  value: string;
  label: string;
  direction: 'asc' | 'desc';
}

interface SortOptions {
  fields: SortField[];
}

interface SortMenuProps {
  appliedSort: SortField | null;
  options: SortOptions;
  onApply: (selected: SortField | null) => void;
  onReset?: () => void;
}

const SortMenu: React.FC<SortMenuProps> = ({
  appliedSort,
  options,
  onApply,
  onReset
}) => {
  const [selectedIndex, setSelectedIndex] = useState<number | null>(null);

  // Sync local state with appliedSort prop
  useEffect(() => {
    if (appliedSort) {
      const idx = options.fields.findIndex(
        f =>
          f.value === appliedSort.value && f.direction === appliedSort.direction
      );
      setSelectedIndex(idx >= 0 ? idx : null);
    } else {
      setSelectedIndex(null);
    }
  }, [appliedSort, options.fields]);

  const handleReset = (onClose: () => void) => {
    setSelectedIndex(null);
    onReset?.();
    onClose();
  };

  const handleApply = (onClose: () => void) => {
    if (selectedIndex !== null && options.fields[selectedIndex] !== undefined) {
      onApply(options.fields[selectedIndex]);
    } else {
      onApply(null);
    }
    onClose();
  };

  return (
    <Menu closeOnSelect={false} ariaLabel="sort-menu">
      {({ onClose }) => (
        <>
          <MenuButton
            as={Button}
            variant="secondary"
            leftIcon={<FontAwesomeIcon icon={faSort} />}
          >
            Sort {appliedSort ? `: ${appliedSort.label}` : ''}
          </MenuButton>

          <MenuList minWidth="240px" p={3}>
            <MenuItem style={{ display: 'none' }} />
            <RadioGroup
              onChange={val => setSelectedIndex(Number(val))}
              value={selectedIndex !== null ? String(selectedIndex) : ''}
            >
              <Stack spacing={3}>
                {options.fields.map((field, index) => (
                  <Radio key={index} value={String(index)}>
                    <Text textStyle="textBody">{field.label}</Text>
                  </Radio>
                ))}
              </Stack>
            </RadioGroup>

            <Flex justify="flex-end" gap={4} mt={4}>
              <ButtonText
                size="regular"
                variant="caution"
                onClick={() => handleReset(onClose)}
              >
                Reset
              </ButtonText>
              <ButtonText
                size="regular"
                variant="primary"
                onClick={() => handleApply(onClose)}
              >
                Apply
              </ButtonText>
            </Flex>
          </MenuList>
        </>
      )}
    </Menu>
  );
};

export default SortMenu;
